import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { buildUrl } from '@repo/toolkit/build-url'
import { NumberFormatter } from '@repo/toolkit/number'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

import { API } from '../../../../api/apis.config'
import { getCenterCostsView } from '../../../../api/centers/get-center-costs-view'
import { Paths } from '../../../../lib/constants/path'
import { useObserver } from '../../../../lib/contexts/observer'
import useTranslate from '../../../../lib/hooks/use-translate'
import { transportModeEnumMapping } from '../../../../lib/types/enums/activity/transport-mode.enum'
import { activityMemberEnumMapping } from '../../../../lib/types/enums/activity-member/activity-member.enum'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { CenterActivity } from '../../../../lib/types/models/center-activity/center-activity'
import { JourneyProperties } from '../../../../lib/types/models/product/journey.properties'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { isHostFamilyDayActivity } from '../../../../utils/constants/activity.constants'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'

type Props = {
  centerId: number
  currency?: CurrencyEnum
  mode?: 'open' | 'validate' | 'view' | 'staff-view'
}

const CenterActivityTable = ({ centerId, currency, mode }: Props) => {
  const { t, currentLanguage } = useTranslate()
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<CenterActivity>>(null)

  const { data, refetch } = useQuery({
    queryKey: ['center-costs-view', centerId],
    queryFn: () => getCenterCostsView(centerId)
  })

  useObserver({
    key: 'ol-center-activity-table',
    update: () => {
      // Refetch the table data and the total costs when an update is observed
      tableRef.current?.refetch()
      refetch()
    }
  })

  // "Host family day" has no local transport, no cost and is not payable: those cells are
  // displayed as "-"
  const isHostFamilyDay = (centerActivity: CenterActivity) =>
    isHostFamilyDayActivity(centerActivity.activity)

  // Opens the activity sheet: the OL edits its own activity, the admin opens the OL activity view.
  // The staff has no access to activity sheets, and "Host family day" has no detail page
  const openActivity = (centerActivity: CenterActivity, event?: React.MouseEvent) => {
    if (isHostFamilyDay(centerActivity)) return
    const { activity } = centerActivity
    if (mode === 'open')
      navigateWithEvent(
        buildUrl(Paths.OL_PERSONAL_SPACE_ACTIVITY_UPDATE_PAGE, { id: activity.id }),
        event
      )
    else if (mode === 'validate' || mode === 'view')
      navigateWithEvent(
        buildUrl(Paths.OL_ACTIVITY_VIEW_PAGE, { id: activity.id, olId: activity.olId }),
        event
      )
  }

  const columns: Column<CenterActivity>[] = [
    {
      enableSorting: true,
      accessorKey: 'activity.name',
      header: t('ol.center.program.activities.table.name')
    },
    {
      enableSorting: true,
      accessorKey: 'numberOfStudents',
      header: t('ol.center.program.activities.table.number.of.students'),
      cell: ({ row }) => <Badge text={row.original.numberOfStudents} variant="fushia" />
    },
    {
      enableSorting: true,
      accessorKey: 'numberOfStaffs',
      header: t('ol.center.program.activities.table.number.of.leaders'),
      cell: ({ row }) => <Badge text={row.original.numberOfStaffs} variant="blue" />
    },
    {
      enableSorting: true,
      accessorKey: 'activity.transportMode',
      header: t('ol.center.program.activities.table.local.transport'),
      cell: ({ row }) =>
        isHostFamilyDay(row.original)
          ? '-'
          : t(transportModeEnumMapping[row.original.activity.transportMode])
    },
    ...(mode !== 'staff-view'
      ? ([
          {
            enableSorting: true,
            accessorKey: 'averageCostPerStudent',
            header: t('ol.center.program.activities.table.average.cost.per.student'),
            centerCell: true,
            cell: ({ row }) =>
              isHostFamilyDay(row.original) ? (
                '-'
              ) : (
                <Badge
                  variant="primary"
                  text={NumberFormatter.formatCurrency(
                    row.original.averageCostPerStudent,
                    currency
                  )}
                />
              )
          } as Column<CenterActivity>
        ] as Column<CenterActivity>[])
      : []),
    {
      enableSorting: true,
      accessorKey: 'totalCostExcludingTransport',
      header: t('ol.center.program.activities.table.total.cost'),
      centerCell: true,
      cell: ({ row }) =>
        isHostFamilyDay(row.original) ? (
          '-'
        ) : (
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(
              row.original.totalCostExcludingTransport,
              currency
            )}
          />
        )
    },
    {
      enableSorting: true,
      accessorKey: 'activityPaidBy',
      header: t('ol.center.program.activities.table.paid.by'),
      cell: ({ row }) =>
        isHostFamilyDay(row.original)
          ? '-'
          : activityMemberEnumMapping[row.original.activityPaidBy]
    },
    {
      accessorKey: 'journeys',
      header: t('ol.center.program.activities.table.programs'),
      cell: ({ row }) => (
        <Typography
          variant="secondary-body"
          color={tailwindColors.grey.DEFAULT}
          className="text-wrap"
        >
          {row.original.journeys
            .map((journey) => JourneyProperties.getProgramLabel(journey, currentLanguage))
            .join(', ')}
        </Typography>
      )
    },
    {
      accessorKey: 'option',
      header: t('ol.center.program.activities.table.option'),
      cell: ({ row }) => getTranslatedName(row.original.option, currentLanguage) || '-'
    }
  ]
  return (
    <DynamicDataTable<CenterActivity>
      ref={tableRef}
      columns={columns}
      onRowClick={mode !== 'staff-view' ? openActivity : undefined}
      footer={
        data && (
          <div
            className={cn('w-full flex justify-between items-center pr-3', {
              'justify-end mobile:justify-start': mode === 'staff-view'
            })}
          >
            {mode !== 'staff-view' && (
              <div className="flex items-center gap-1">
                <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                  {t('ol.center.program.activities.table.total.activity.cost')} :
                </Typography>
                <Badge
                  variant="warning"
                  text={NumberFormatter.formatCurrency(data.totalActivitiesCost, currency)}
                />
              </div>
            )}
            <div className="flex items-center gap-4">
              {mode !== 'staff-view' && (
                <div className="flex items-center gap-1">
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {t('ol.center.program.activities.table.part.paid.by.local.organizer')} :
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {NumberFormatter.formatCurrency(data.totalLocalOrganizerCost, currency)}
                  </Typography>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {t('ol.center.program.activities.table.part.paid.by.french.leader')} :
                </Typography>
                {mode === 'staff-view' ? (
                  <Badge
                    variant="warning"
                    text={NumberFormatter.formatCurrency(data.totalFrenchLeaderCost, currency)}
                  />
                ) : (
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {NumberFormatter.formatCurrency(data.totalFrenchLeaderCost, currency)}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        )
      }
      footerclassName="bg-white-dark"
      paginated={false}
      headerType="none"
      endpoint={API.centers.getCenterActivities.url.replace(':id', centerId.toString())}
    />
  )
}

export default CenterActivityTable
